import React, { useMemo, useState, useEffect } from 'react';
import { MSRoadMapManager } from '../utils/MSRoadMapManager';
import type { M365ReleaseItem, M365SortField, M365Product, M365Tag, M365TagContainer } from '../types/m365';

function formatDate(value: string | undefined): string {
    if (!value) return '—';
    try {
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    } catch {
        return value;
    }
}

function tagDisplayValue(t: M365Tag): string {
    return typeof t === 'string' ? t : (t?.tagName ?? '');
}

function itemMatchesProducts(item: M365ReleaseItem, productNames: string[]): boolean {
    if (!productNames.length) return true;
    const container = (item.tagContainer ?? item.tagsContainer) as M365TagContainer | undefined;
    const products = container?.products ?? [];
    const names = new Set(products.map((p: M365Product) => MSRoadMapManager.getInstance().getProductDisplayName(p)));
    return productNames.some((name) => names.has(name));
}

function itemMatchesReleasePhase(item: M365ReleaseItem, phaseNames: string[]): boolean {
    if (!phaseNames.length) return true;
    const container = (item.tagContainer ?? item.tagsContainer) as M365TagContainer | undefined;
    const releasePhase = container?.releasePhase;
    if (!Array.isArray(releasePhase)) return false;
    const itemPhases = new Set(
        releasePhase.map((entry) => {
            const o = entry as { tagName?: string };
            return typeof o?.tagName === 'string' ? o.tagName.trim() : '';
        }).filter(Boolean)
    );
    return phaseNames.some((name) => itemPhases.has(name));
}

function firstNWords(text: string | undefined, n: number): string {
    if (!text || typeof text !== 'string') return '';
    const words = text.trim().split(/\s+/).filter(Boolean);
    return words.slice(0, n).join(' ') + (words.length > n ? '…' : '');
}

/** Assignment table: product display name -> Bootstrap badge class (badge bg-xxx). */
const PRODUCT_BADGE_CLASS: Record<string, string> = {
    'Microsoft Teams': 'badge bg-primary',
    'SharePoint': 'badge bg-success',
    'Outlook': 'badge bg-info',
    'OneDrive': 'badge bg-warning text-dark',
    'Excel': 'badge bg-success',
    'Word': 'badge bg-primary',
    'PowerPoint': 'badge bg-danger',
    'Exchange': 'badge bg-info',
    'Yammer': 'badge bg-secondary',
    'Viva': 'badge bg-primary',
    'Microsoft 365': 'badge bg-dark',
    'Windows': 'badge bg-secondary',
    'Edge': 'badge bg-info',
    'Defender': 'badge bg-danger',
    'Intune': 'badge bg-primary',
    'Azure': 'badge bg-info',
    'Planner': 'badge bg-success',
    'To Do': 'badge bg-warning text-dark',
    'Forms': 'badge bg-info',
    'Stream': 'badge bg-danger',
    'Sway': 'badge bg-secondary',
};

const DEFAULT_PRODUCT_BADGE = 'badge bg-secondary';

function getBadgeClassForProduct(displayName: string): string {
    return PRODUCT_BADGE_CLASS[displayName] ?? DEFAULT_PRODUCT_BADGE;
}

function getProductNamesLine(item: M365ReleaseItem): string {
    const container = (item.tagContainer ?? item.tagsContainer) as M365TagContainer | undefined;
    const products = container?.products ?? [];
    if (!products.length) return '—';
    return products
        .map((p) => {
            const name = MSRoadMapManager.getInstance().getProductDisplayName(p);
            if (!name) return '';
            const badgeClass = getBadgeClassForProduct(name);
            return `<span class="${badgeClass} me-1">${escapeHtml(name)}</span>`;
        })
        .filter(Boolean)
        .join(' ') || '—';
}

function escapeHtml(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function itemMatchesStatus(item: M365ReleaseItem, statusFilter: string[]): boolean {
    if (!statusFilter.length) return true;
    const itemStatus = typeof item.status === 'string' ? item.status.trim() : '';
    return statusFilter.includes(itemStatus);
}

function removeProductNameFromTitle(title: string): string {
    if (title.includes(':')) {
        return title.split(':')[1].trim();
    }
    return title;
}

function compareItems(a: M365ReleaseItem, b: M365ReleaseItem, sortField: M365SortField): number {
    const av = a[sortField];
    const bv = b[sortField];
    const aStr = typeof av === 'string' ? av : (av != null ? String(av) : '');
    const bStr = typeof bv === 'string' ? bv : (bv != null ? String(bv) : '');
    if (sortField === 'title' || sortField === 'status') {
        return aStr.localeCompare(bStr, undefined, { sensitivity: 'base' });
    }
    const aDate = new Date(aStr).getTime();
    const bDate = new Date(bStr).getTime();
    return aDate - bDate;
}

export interface M365UpdateListProps {
    items: M365ReleaseItem[];
    loading?: boolean;
    error?: string | null;
    /** Initial product filter: only show items that have at least one of these products. */
    initialProductFilter?: string[];
    /** Initial release phase filter: only show items that have at least one of these release phases (tagsContainer.releasePhase[].tagName). */
    initialReleasePhaseFilter?: string[];
    /** Optional pre-filter by status: only show items whose status is in this array. */
    initialStatusFilter?: string[];
    /** Maximum number of items per page. */
    maxItems?: number;
    /** Sort field for the result list. */
    sortField?: M365SortField;
    /** Display mode: default (full cards) or compact (list like trending-videos). */
    viewMode?: 'default' | 'compact';
}

function M365UpdateList({
    items,
    loading = false,
    error = null,
    initialProductFilter = [],
    initialReleasePhaseFilter = [],
    initialStatusFilter = [],
    maxItems = 10,
    sortField = 'modified',
    viewMode = 'default'
}: M365UpdateListProps) {
    const [page, setPage] = useState(0);

    const filteredSorted = useMemo(() => {
        let list = items
            .filter((item) => itemMatchesProducts(item, initialProductFilter))
            .filter((item) => itemMatchesReleasePhase(item, initialReleasePhaseFilter))
            .filter((item) => itemMatchesStatus(item, initialStatusFilter));
        list = [...list].sort((a, b) => compareItems(b, a, sortField));
        return list;
    }, [items, initialProductFilter, initialReleasePhaseFilter, initialStatusFilter, sortField]);

    const totalPages = Math.max(1, Math.ceil(filteredSorted.length / maxItems));
    const currentPage = Math.min(page, totalPages - 1);

    useEffect(() => {
        setPage(0);
    }, [filteredSorted.length, sortField, maxItems]);
    const start = currentPage * maxItems;
    const pageItems = filteredSorted.slice(start, start + maxItems);

    if (loading) {
        return (
            <div className="m365-update-list loading d-flex align-items-center justify-content-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading…</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="m365-update-list error alert alert-danger" role="alert">
                {error}
            </div>
        );
    }

    if (!filteredSorted.length) {
        return (
            <div className="m365-update-list empty text-muted text-center py-5">
                No updates found. Try adjusting your filters.
            </div>
        );
    }

    if (viewMode === 'compact') {
        return (
            <div className="roadmap-compact-list">
                <h2>Upcoming Features</h2>
                <ul className="roadmap-entry">
                    {pageItems.map((item, index) => (
                        <li key={item.id ?? `${item.title}-${start + index}`}>
                            <div className="trending-m365-compact-item">
                            <span className="number">{start + index + 1}</span>
                                <h3 className='mb-2'>{removeProductNameFromTitle(item.title ?? 'Untitled')}</h3>
                                <div className="post-meta">
                                    <span className="date">{formatDate(item.modified)}</span> 
                                    <span className="mx-1">•</span>                                    
                                    <span>{item.status ?? '—'}</span>
                                </div>
                                <p className="trending-m365-compact-description small text-muted mb-1">
                                    {firstNWords(item.description, 10)}
                                </p>
                                <div className="trending-m365-compact-meta small">
                                    <span className="me-2">
                                        <strong>Products:</strong><br /> 
                                        <span dangerouslySetInnerHTML={{ __html: getProductNamesLine(item) }} />
                                    </span>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
                {totalPages > 1 && (
                    <nav
                        className="m365-update-pagination mt-4 d-flex justify-content-center align-items-center gap-2 flex-wrap"
                        aria-label="Pagination"
                    >
                        <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            disabled={currentPage === 0}
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                        >
                            Previous
                        </button>
                        <span className="small text-muted">
                            Page {currentPage + 1} of {totalPages}
                        </span>
                        <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            disabled={currentPage >= totalPages - 1}
                            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                        >
                            Next
                        </button>
                    </nav>
                )}
            </div>
        );
    }

    return (
        <div className="m365-update-list">
            <div className="d-flex flex-column gap-4">
                {pageItems.map((item, index) => (
                    <article
                        key={item.id ?? `${item.title}-${start + index}`}
                        className="m365-update-card card border-0 shadow-sm"
                    >
                        <div className="card-body">
                            <h3 className="card-title h5">{removeProductNameFromTitle(item.title ?? 'Untitled')}</h3>
                            <div className="m365-update-meta small text-muted mb-2">
                                <span title="Public disclosure date">
                                    {formatDate(item.publicDisclosureAvailabilityDate)}
                                </span>
                                <span className="mx-2">·</span>
                                <span>{item.status ?? '—'}</span>
                                <span className="mx-2">·</span>
                                <span title="Last modified">{formatDate(item.modified)}</span>
                            </div>
                            {item.description && (
                                <div className="m365-update-description mb-3">
                                    {item.description}
                                </div>
                            )}
                            {Array.isArray(item.tags) && item.tags.length > 0 && (
                                <div className="m365-update-tags mb-3">
                                    <strong className="small text-muted">Tags:</strong>{' '}
                                    {item.tags.map(tagDisplayValue).join(', ')}
                                </div>
                            )}
                            {(() => {
                                const tagContainer = item.tagContainer ?? item.tagsContainer;
                                if (!tagContainer || Object.keys(tagContainer).length === 0) return null;
                                const entries = Object.entries(tagContainer);
                                return (
                                    <div className="m365-update-products border rounded p-3 bg-light">
                                        <h4 className="h6 mb-3 text-center">Products information</h4>
                                        <dl className="mb-0 small m365-update-products-dl">
                                            {entries.map(([key, value]) => (
                                                <React.Fragment key={key}>
                                                    <dt className="text-muted">{key}</dt>
                                                    <dd className="mb-0">
                                                        {Array.isArray(value) ? (
                                                            (key === 'products'
                                                                ? value.map((v, i) => {
                                                                      const name =
                                                                          typeof v === 'object' && v !== null
                                                                              ? MSRoadMapManager.getInstance().getProductDisplayName(v as M365Product)
                                                                              : String(v);
                                                                      const badgeClass = name ? getBadgeClassForProduct(name) : DEFAULT_PRODUCT_BADGE;
                                                                      return (
                                                                          <span key={i} className={`${badgeClass} me-1`}>
                                                                              {name || String(v)}
                                                                          </span>
                                                                      );
                                                                  })
                                                                : value.map((v, i) => (
                                                                      <span key={i}>
                                                                          {typeof v === 'object' && v !== null
                                                                              ? MSRoadMapManager.getInstance().getProductDisplayName(v as M365Product)
                                                                              : String(v)}
                                                                      </span>
                                                                  )).reduce<React.ReactNode[]>(
                                                                      (acc, el, i) =>
                                                                          i === 0 ? [el] : [...acc, ', ', el],
                                                                      []
                                                                  )
                                                            )
                                                        ) : typeof value === 'object' && value !== null ? (
                                                            <span>{(value as Record<string, unknown>).tagName as string}</span>
                                                        ) : (
                                                            <span>{String(value)}</span>
                                                        )}
                                                    </dd>
                                                </React.Fragment>
                                            ))}
                                        </dl>
                                    </div>
                                );
                            })()}
                        </div>
                    </article>
                ))}
            </div>

            {totalPages > 1 && (
                <nav
                    className="m365-update-pagination mt-4 d-flex justify-content-center align-items-center gap-2 flex-wrap"
                    aria-label="Pagination"
                >
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        disabled={currentPage === 0}
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                    >
                        Previous
                    </button>
                    <span className="small text-muted">
                        Page {currentPage + 1} of {totalPages}
                    </span>
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        disabled={currentPage >= totalPages - 1}
                        onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    >
                        Next
                    </button>
                </nav>
            )}
        </div>
    );
}

export default M365UpdateList;
